import type { ConversationAgentSummary } from "~/services/agents";
import { AgentSwitchMarker } from "./AgentSwitchMarker";
import { AssistantTurn } from "./AssistantTurn";
import { UserMessage } from "./UserMessage";
import type { AnswerChannel } from "./awaitingInput";
import { turnAnchorId } from "./useAnchoredScroll";
import type { Turn } from "./useTurns";

interface ConversationTurnsProps {
  turns: Turn[];
  // Conversation-level agent identity for assistant avatars and turn labels;
  // null for legacy agentless conversations. Narrowed to the label + avatar
  // subset so read-only views can supply it without the agent's full config or
  // author metadata. Used for a turn with no recorded handler at all (a legacy
  // agentless row) — never to stand in for a handler that has since left the
  // project, which renders with no borrowed identity instead.
  conversationAgent: ConversationAgentSummary | null;
  // Resolves a turn's `agentId` to the agent that handled it, so each turn is
  // labeled with its own handler rather than the conversation's current bound
  // agent. A recorded id that does not resolve here (a since-detached handler)
  // renders unlabeled rather than borrowing the bound agent. Omitted by
  // read-only views, which carry the per-turn `agent` summary instead.
  agentsById?: ReadonlyMap<string, ConversationAgentSummary>;
  conversationId: string;
  // Live-only: surfaces the "Stopping" label on the in-flight turn.
  isCancelling?: boolean;
  // Read-only feedback rendering (admin review).
  readOnlyFeedback?: boolean;
  // Resume a paused human-in-the-loop tool call. Omitted in read-only views,
  // where the cards render but cannot be answered.
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // Tool-call ids genuinely paused awaiting human input, with the channel that
  // answers each; gates answerability of the HITL and fallback cards.
  awaitingToolCalls?: ReadonlyMap<string, AnswerChannel>;
  // Turn id a deep-link scroll just landed on; flashes that user message.
  highlightedTurnId?: string | null;
  // Reveal the hover copy-link-to-turn affordance on assistant turns. On for
  // the read-only shared/admin views; off for the live regular conversation.
  showTurnLinks?: boolean;
}

// The single user/assistant turn dispatch shared by the live `Chat` and the
// read-only (shared / admin) views. Keeping it in one place means new turn
// kinds and per-turn rendering rules reach every surface automatically rather
// than having to be mirrored into each caller. Callers own their own scroll
// container and `<ol>` wrapper.
export function ConversationTurns({
  turns,
  conversationAgent,
  agentsById,
  conversationId,
  isCancelling,
  readOnlyFeedback,
  onAnswer,
  awaitingToolCalls,
  highlightedTurnId,
  showTurnLinks,
}: ConversationTurnsProps) {
  return (
    <>
      {turns.map((turn) => {
        if (turn.kind === "user") {
          const anchorId = turnAnchorId(turn.turnId);
          return (
            <UserMessage
              key={turn.key}
              text={turn.text}
              attachmentIds={turn.attachmentIds}
              turnId={anchorId}
              highlighted={!!anchorId && anchorId === highlightedTurnId}
            />
          );
        }
        if (turn.kind === "agent-switch") {
          const fromName =
            turn.fromAgent?.display_name ??
            (turn.fromAgentId ? agentsById?.get(turn.fromAgentId)?.display_name : undefined);
          const toName =
            turn.toAgent?.display_name ??
            (turn.toAgentId ? agentsById?.get(turn.toAgentId)?.display_name : undefined);
          return <AgentSwitchMarker key={turn.key} fromAgent={fromName} toAgent={toName} />;
        }
        // A recorded `agentId` that no longer resolves against the roster is an
        // agent detached from the project since it took this turn: render it with
        // no borrowed identity rather than the conversation's current bound agent
        // — mirroring the backend's "a different agent no longer in this project"
        // degrade, never claiming a departed peer's turn for whoever is bound now.
        // Only a turn with no recorded handler (a legacy agentless row) falls back
        // to the conversation agent; shared reads carry `turn.agent` and skip this.
        const turnAgent = turn.agentId
          ? (agentsById?.get(turn.agentId) ?? null)
          : conversationAgent;
        return (
          <AssistantTurn
            key={turn.key}
            turn={turn}
            agent={turn.agent ?? turnAgent}
            conversationId={conversationId}
            isCancelling={isCancelling}
            readOnlyFeedback={readOnlyFeedback}
            onAnswer={onAnswer}
            awaitingToolCalls={awaitingToolCalls}
            showTurnLinks={showTurnLinks}
          />
        );
      })}
    </>
  );
}
